/**
 * Futuristic UI Components for RNCM Productivity
 * Neon glowing cards, headers, and metrics with vibrant colors
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Futuristic gradient presets
export const NEON_GRADIENTS = {
  cyan: 'from-cyan-400 via-cyan-500 to-blue-600',
  magenta: 'from-fuchsia-500 via-pink-500 to-rose-500',
  emerald: 'from-emerald-400 via-green-500 to-teal-600',
  amber: 'from-amber-400 via-orange-500 to-red-500',
  violet: 'from-violet-500 via-purple-600 to-indigo-700',
  rainbow: 'from-pink-500 via-purple-500 to-cyan-500',
  sunset: 'from-orange-400 via-rose-500 to-purple-600',
  ocean: 'from-cyan-400 via-blue-500 to-indigo-600',
  forest: 'from-lime-400 via-emerald-500 to-teal-600',
  fire: 'from-yellow-400 via-orange-500 to-red-600'
};

// Glowing neon card with glassmorphism
export function NeonCard({ 
  children, 
  className, 
  glowColor = 'cyan',
  variant = 'glass',
  animated = false,
  ...props 
}) {
  const glowColors = {
    cyan: 'shadow-cyan-500/30 hover:shadow-cyan-400/50',
    magenta: 'shadow-fuchsia-500/30 hover:shadow-fuchsia-400/50',
    emerald: 'shadow-emerald-500/30 hover:shadow-emerald-400/50',
    amber: 'shadow-amber-500/30 hover:shadow-amber-400/50',
    violet: 'shadow-violet-500/30 hover:shadow-violet-400/50'
  };

  const variants = {
    glass: 'bg-white/10 dark:bg-slate-900/50 backdrop-blur-xl border border-white/20',
    solid: 'bg-slate-900 border border-slate-700',
    gradient: `bg-gradient-to-br ${NEON_GRADIENTS[glowColor]} border-0`
  };

  return (
    <Card 
      className={cn(
        variants[variant],
        `shadow-2xl ${glowColors[glowColor]}`,
        'transition-all duration-500 hover:-translate-y-1',
        animated && 'animate-glow',
        className
      )}
      {...props}
    >
      {children}
    </Card>
  );
}

// Futuristic metric card with neon glow
export function NeonMetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  gradient = 'cyan',
  trend,
  trendValue,
  className
}) {
  const glowMap = {
    cyan: { border: 'border-cyan-400/50', shadow: '0 0 30px rgba(34, 211, 238, 0.3)', iconBg: 'bg-cyan-500/20', iconColor: 'text-cyan-400' },
    magenta: { border: 'border-fuchsia-400/50', shadow: '0 0 30px rgba(232, 121, 249, 0.3)', iconBg: 'bg-fuchsia-500/20', iconColor: 'text-fuchsia-400' },
    emerald: { border: 'border-emerald-400/50', shadow: '0 0 30px rgba(52, 211, 153, 0.3)', iconBg: 'bg-emerald-500/20', iconColor: 'text-emerald-400' },
    amber: { border: 'border-amber-400/50', shadow: '0 0 30px rgba(251, 191, 36, 0.3)', iconBg: 'bg-amber-500/20', iconColor: 'text-amber-400' },
    violet: { border: 'border-violet-400/50', shadow: '0 0 30px rgba(167, 139, 250, 0.3)', iconBg: 'bg-violet-500/20', iconColor: 'text-violet-400' },
    rose: { border: 'border-rose-400/50', shadow: '0 0 30px rgba(251, 113, 133, 0.3)', iconBg: 'bg-rose-500/20', iconColor: 'text-rose-400' }
  };

  const glow = glowMap[gradient] || glowMap.cyan;

  return (
    <Card 
      className={cn(
        'relative overflow-hidden',
        'bg-slate-900/80 backdrop-blur-xl',
        `border ${glow.border}`,
        'transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02]',
        'group cursor-default',
        className
      )}
      style={{ boxShadow: glow.shadow }}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${NEON_GRADIENTS[gradient]} opacity-5 group-hover:opacity-10 transition-opacity duration-500`} />
      
      {/* Scanning line effect */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent animate-scan" />
      </div>

      <CardContent className="pt-6 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400 font-medium tracking-wide uppercase">{title}</p>
            <p className="text-4xl font-bold text-white mt-2 tracking-tight" style={{ textShadow: '0 0 20px rgba(255,255,255,0.2)' }}>
              {value}
            </p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-rose-400' : 'text-slate-400'}`}>
                <span>{trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}</span>
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className={`p-4 rounded-2xl ${glow.iconBg} backdrop-blur-sm`}>
              <Icon className={`h-8 w-8 ${glow.iconColor}`} style={{ filter: 'drop-shadow(0 0 10px currentColor)' }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Futuristic page header with animated gradient
export function NeonHeader({ 
  icon: Icon, 
  title, 
  subtitle, 
  children,
  gradient = 'from-cyan-500 via-purple-500 to-pink-500'
}) {
  return (
    <div className="relative overflow-hidden rounded-3xl p-8 text-white">
      {/* Animated gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-r ${gradient} animate-gradient-x`} />
      
      {/* Mesh overlay */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                          radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />
      
      {/* Glass overlay */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />
      
      {/* Glow effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight flex items-center gap-4">
            {Icon && (
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                <Icon className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
              </div>
            )}
            <span style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>{title}</span>
          </h1>
          {subtitle && (
            <p className="text-white/80 mt-3 text-lg max-w-xl">{subtitle}</p>
          )}
        </div>
        {children && (
          <div className="flex flex-wrap gap-3">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// Futuristic section header
export function NeonSectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="p-2 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl backdrop-blur-sm">
            <Icon className="h-6 w-6 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.6))' }} />
          </div>
        )}
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

// Glowing progress bar
export function NeonProgress({ value, max = 100, color = 'cyan', showLabel = true, size = 'md' }) {
  const percentage = Math.min((value / max) * 100, 100);
  
  const colors = {
    cyan: { bg: 'bg-cyan-500', glow: 'shadow-cyan-500/50' },
    emerald: { bg: 'bg-emerald-500', glow: 'shadow-emerald-500/50' },
    amber: { bg: 'bg-amber-500', glow: 'shadow-amber-500/50' },
    rose: { bg: 'bg-rose-500', glow: 'shadow-rose-500/50' },
    violet: { bg: 'bg-violet-500', glow: 'shadow-violet-500/50' }
  };

  const sizes = { sm: 'h-1', md: 'h-2', lg: 'h-3' };
  const c = colors[color] || colors.cyan;

  return (
    <div className="w-full">
      <div className={`w-full bg-slate-800 rounded-full ${sizes[size]} overflow-hidden`}>
        <div 
          className={`${sizes[size]} ${c.bg} rounded-full transition-all duration-700 ease-out shadow-lg ${c.glow}`}
          style={{ width: `${percentage}%`, boxShadow: `0 0 20px currentColor` }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1 text-xs text-slate-400">
          <span>{value}</span>
          <span>{percentage.toFixed(0)}%</span>
        </div>
      )}
    </div>
  );
}

// Animated stat number
export function AnimatedStat({ value, suffix = '', prefix = '', className }) {
  return (
    <span className={cn('tabular-nums font-bold', className)} style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>
      {prefix}{value}{suffix}
    </span>
  );
}

// Futuristic table wrapper
export function NeonTableWrapper({ children, className }) {
  return (
    <div className={cn(
      'relative overflow-hidden rounded-2xl',
      'bg-slate-900/60 backdrop-blur-xl',
      'border border-slate-700/50',
      'shadow-2xl shadow-cyan-500/10',
      className
    )}>
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent" />
      <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-purple-500/20 to-transparent" />
      
      {children}
    </div>
  );
}
